import type { ComponentNode } from '@canvas-builder/types/canvas'
import { CanvasHistoryAction, ComponentTypes } from '@canvas-builder/types/canvas'
import {
  getComponentTitle,
  getNodeDisplayNameByComponentType,
  scrollToComponentById,
} from '@canvas-builder/util/canvas.util'
import { findNodeInTreeById, findParentByNodeId } from '@canvas-builder/util/tree'
import type { ComputedRef, Ref } from 'vue'
import { computed, inject, ref, nextTick } from 'vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import { useI18n } from 'vue-i18n'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import useCanvasHistory from '@canvas-builder/composables/useCanvasHistory'

const activeComponentId: Ref<ComponentNode['id'] | null> = ref(null)
const isContentVisible: Ref<boolean> = ref(false)
const isolatedScribbleChildId: Ref<ComponentNode['id'] | null> = ref(null)

export default function useReorderComponent() {
  const activeComponent: Ref<ComponentNode | null> = computed(() =>
    activeComponentId.value ? findNodeInTreeById(canvasContent.value, activeComponentId.value) : null,
  )

  const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
    'launchDarkly',
    computed(() => ({})),
  )

  const { t } = useI18n()
  const {
    canvasContent,
    setCanvasContent,
    setComponentSelectedMode,
    removeComponent: canvasRemoveComponent,
  } = useCanvas()
  const { addCanvasHistoryItem } = useCanvasHistory()
  const isScribbleIsolationEnabled = computed(() => launchDarkly.value?.enable_reorder_scribble_isolation ?? false)

  const activeTitle = computed(() =>
    activeComponent.value ? getTitle(activeComponent.value) : t('canvasUI.canvasBuilder.reorder.title'),
  )

  const setActiveComponentId = (id: string | null) => {
    activeComponentId.value = id
  }

  const resetActiveComponentId = () => {
    setActiveComponentId(null)
  }

  const shouldShowOnlyIsolatedChild = (scribbleId: string) => {
    if (!isScribbleIsolationEnabled.value || !isolatedScribbleChildId.value) return false

    const scribbleComponent = findNodeInTreeById(canvasContent.value, scribbleId)
    if (!scribbleComponent || scribbleComponent.type !== ComponentTypes.Scribble) return false

    return scribbleComponent.children?.some((child) => child.id === isolatedScribbleChildId.value) || false
  }

  const getFilteredScribbleChildren = (scribbleId: string, children: ComponentNode[]) => {
    if (shouldShowOnlyIsolatedChild(scribbleId)) {
      return children.filter((child) => child.id === isolatedScribbleChildId.value)
    }
    return children
  }

  const activeChildren = computed({
    get: () => activeComponent.value?.children ?? canvasContent.value,
    set: (value: ComponentNode[]) => {
      setCanvasContent({ component: activeComponent.value, content: value })

      addCanvasHistoryItem({
        action: CanvasHistoryAction.REORDER,
        componentType: ComponentTypes.Root,
      })
    },
  })

  const getTitle = (component: ComponentNode) => {
    let title = ''

    if (component.type === ComponentTypes.GridItem) {
      const displayName = getNodeDisplayNameByComponentType(component) || getComponentTitle(ComponentTypes.GridItem, t)

      title = `${displayName} (${component?.children?.length ?? 0})`
    } else {
      const displayName = getNodeDisplayNameByComponentType(component)

      title = displayName && displayName !== component.type ? displayName : getComponentTitle(component.type, t)
    }

    return title
  }

  const selectParentComponent = (component: ComponentNode) => {
    const parent = findParentByNodeId(canvasContent.value, component.id)

    // Only handle scribble isolation if the feature flag is enabled
    if (
      isScribbleIsolationEnabled.value &&
      component.type === ComponentTypes.Scribble &&
      isolatedScribbleChildId.value
    ) {
      isolatedScribbleChildId.value = null
    }

    setActiveComponentId(parent?.id ?? null)
  }

  const toggleContentVisibility = () => {
    isContentVisible.value = !isContentVisible.value
  }

  const initialize = () => {
    isContentVisible.value = false
    resetActiveComponentId()
    // Reset isolation state when initializing (only if feature is enabled)
    if (isScribbleIsolationEnabled.value) {
      isolatedScribbleChildId.value = null
    }
  }

  const removeComponent = (component: ComponentNode) => {
    canvasRemoveComponent(component.id)

    // If we're removing the isolated component, reset isolation (only if feature is enabled)
    if (isScribbleIsolationEnabled.value && isolatedScribbleChildId.value === component.id) {
      isolatedScribbleChildId.value = null
    }

    resetActiveComponentId()
  }

  const renameComponent = (component: ComponentNode, newName: string) => {
    if (!newName || !newName.trim()) {
      return
    }

    const targetComponent = findNodeInTreeById(canvasContent.value, component.id)
    if (targetComponent) {
      targetComponent.tracking_id = newName.trim()

      addCanvasHistoryItem({
        action: CanvasHistoryAction.REORDER,
        componentType: component.type,
      })
    } else {
      console.error('Target component not found for renaming.')
    }
  }

  const selectComponent = (component: ComponentNode) => {
    // Only handle scribble isolation if the feature flag is enabled
    if (isScribbleIsolationEnabled.value) {
      const parent = findParentByNodeId(canvasContent.value, component.id)

      if (parent?.type === ComponentTypes.Scribble) {
        if (isolatedScribbleChildId.value === component.id) {
          isolatedScribbleChildId.value = null
        } else {
          isolatedScribbleChildId.value = component.id
        }
      } else {
        isolatedScribbleChildId.value = null
      }
    }

    // First scroll to the component, then select it after Vue's next rendering cycle
    // This is especially important for Text components where toolbar positioning depends on
    // the component being fully rendered and in its final scroll position
    scrollToComponentById(component.id)

    nextTick(() => {
      setComponentSelectedMode(component.id)
    })
  }

  // Helper function to check if a component is the currently isolated scribble child
  const isComponentIsolated = (componentId: string) => {
    return isScribbleIsolationEnabled.value && isolatedScribbleChildId.value === componentId
  }

  return {
    activeComponent,
    activeChildren,
    activeTitle,
    isContentVisible,
    initialize,
    getTitle,
    selectParentComponent,
    setActiveComponentId,
    selectComponent,
    resetActiveComponentId,
    toggleContentVisibility,
    removeComponent,
    renameComponent,
    isolatedScribbleChildId: computed(() => isolatedScribbleChildId.value),
    shouldShowOnlyIsolatedChild,
    getFilteredScribbleChildren,
    isComponentIsolated,
  }
}
